/**
 * GeneralSettingsPageV2 — redesigned "General App Settings".
 *
 * Each general_info row is a stand-alone card with:
 *   • header (key as mono identifier + per-row Save + Delete)
 *   • two side-by-side FieldBlocks: Value (TextInput, or UploadTile
 *     when the key contains "image") + Notes (Textarea)
 *
 * Top-right "+ New Setting" header action opens a Modal asking for
 * a key name; on confirm it dispatches createGeneralInfo via the
 * existing action.
 *
 * Same Redux + apiV2 plumbing as v1: fetchGeneralInfo /
 * updateGeneralInfo / deleteGeneralInfo / createGeneralInfo.
 */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import {
  fetchGeneralInfo,
  updateGeneralInfo,
  deleteGeneralInfo,
  createGeneralInfo,
} from '../../../../actions/generalInfoActions';
import { uploadImage } from '../../../../actions/mediaActions';
import LoadingBackdrop from '../../../Common/LoadingBackdrop';
import {
  PageHeader,
  Button,
  Modal,
  IconButton,
  cn,
} from '../../../redesign';
import {
  FieldBlock,
  TextInput,
  Textarea,
  UploadTile,
} from '../../../redesign/edit';

const PlusSign = (
  <span style={{ fontSize: 16, lineHeight: 1 }} aria-hidden>+</span>
);

const SaveIconSvg = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
    <polyline points="17 21 17 13 7 13 7 21" />
    <polyline points="7 3 7 8 15 8" />
  </svg>
);

const TrashIconSvg = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
  </svg>
);

function isImageKey(key) {
  return /image/i.test(key || '');
}

/* ─── Single setting card ──────────────────────────────────────── */

function SettingCard({ settingKey, value, notes, onChange, onNotesChange, onSave, onDelete, onUpload, onRemoveImage }) {
  return (
    <section
      className="bg-surface border border-sand"
      style={{
        borderRadius: 12,
        padding: 18,
        marginBottom: 16,
        minWidth: 0,
      }}
    >
      <div
        className="flex items-center justify-between"
        style={{ marginBottom: 12, gap: 8 }}
      >
        <code
          className="font-mono text-navy truncate"
          style={{ fontSize: 12.5, fontWeight: 600 }}
          title={settingKey}
        >
          {settingKey}
        </code>
        <div className="flex items-center" style={{ gap: 4 }}>
          <IconButton
            size="sm"
            aria-label="Save"
            title="Save"
            onClick={onSave}
          >
            {SaveIconSvg}
          </IconButton>
          <IconButton
            size="sm"
            aria-label="Delete"
            title="Delete"
            onClick={onDelete}
          >
            {TrashIconSvg}
          </IconButton>
        </div>
      </div>

      <div
        className="grid grid-cols-1 md:grid-cols-2"
        style={{ gap: 16 }}
      >
        <FieldBlock label="Value" noMargin>
          {isImageKey(settingKey) ? (
            <UploadTile
              label=""
              value={value}
              accept="image/*"
              onChange={onUpload}
              onRemove={onRemoveImage}
              emptyText="Click to upload image"
            />
          ) : (
            <TextInput
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
            />
          )}
        </FieldBlock>
        <FieldBlock label="Notes" noMargin>
          <Textarea
            value={notes || ''}
            onChange={(e) => onNotesChange(e.target.value)}
            rows={3}
            placeholder="Add notes…"
          />
        </FieldBlock>
      </div>
    </section>
  );
}

/* ─── Main ─────────────────────────────────────────────────────── */

export default function GeneralSettingsPageV2() {
  const dispatch = useDispatch();
  const generalInfo = useSelector(
    (s) => s.general_info?.general_info || [],
  );

  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({});

  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        await dispatch(fetchGeneralInfo());
      } catch {
        Swal.fire({ icon: 'error', title: 'Error loading settings' });
      } finally {
        setLoading(false);
      }
    })();
  }, [dispatch]);

  useEffect(() => {
    if (!generalInfo.length) return;
    const map = {};
    generalInfo.forEach((row) => {
      map[row.general_info_key] = {
        value: row.general_info_value,
        notes: row.general_info_notes,
      };
    });
    setSettings(map);
  }, [generalInfo]);

  /* ─── Per-card handlers ───────────────────────────────────── */

  const setField = (key, patch) =>
    setSettings((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));

  const handleValueChange = (key) => (v) => setField(key, { value: v });
  const handleNotesChange = (key) => (v) => setField(key, { notes: v });

  const handleSave = (key) => async () => {
    try {
      await dispatch(
        updateGeneralInfo({
          general_info_key: key,
          general_info_value: settings[key].value,
          general_info_notes: settings[key].notes,
        }),
      );
      Swal.fire({
        icon: 'success',
        title: `"${key}" saved`,
        timer: 1200,
        showConfirmButton: false,
      });
    } catch {
      Swal.fire({ icon: 'error', title: `Error saving "${key}"` });
    }
  };

  const handleDelete = (key) => () => {
    Swal.fire({
      title: 'Delete setting?',
      text: `"${key}" will be removed. This can't be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      confirmButtonColor: '#d33',
      cancelButtonText: 'Cancel',
    }).then(async (r) => {
      if (!r.isConfirmed) return;
      try {
        await dispatch(deleteGeneralInfo(key));
      } catch {
        Swal.fire({ icon: 'error', title: `Error deleting "${key}"` });
      }
    });
  };

  const handleImageUpload = (key) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    dispatch(uploadImage(file)).then((response) => {
      const url = response?.data?.image_url;
      if (url) setField(key, { value: url });
    });
  };

  const handleImageRemove = (key) => () => setField(key, { value: '' });

  /* ─── Create new ─────────────────────────────────────────── */

  const handleCreateNew = async () => {
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    try {
      await dispatch(
        createGeneralInfo({
          general_info_key: name,
          general_info_value: '',
          general_info_notes: '',
        }),
      );
      Swal.fire({
        icon: 'success',
        title: `Created "${name}"`,
        timer: 1200,
        showConfirmButton: false,
      });
      setNewDialogOpen(false);
      setNewName('');
    } catch {
      Swal.fire({ icon: 'error', title: 'Error creating setting' });
    } finally {
      setCreating(false);
    }
  };

  const totalLabel = generalInfo.length
    ? `${generalInfo.length.toLocaleString()} settings`
    : undefined;

  return (
    <div className="bg-bg min-h-full">
      <LoadingBackdrop open={false} />
      <PageHeader
        eyebrow="Settings & Admin"
        title="General App Settings"
        total={totalLabel}
        actions={
          <Button
            variant="coral"
            leadingIcon={PlusSign}
            onClick={() => setNewDialogOpen(true)}
          >
            New setting
          </Button>
        }
      />

      <div style={{ padding: '0 28px 60px' }}>
        {loading ? (
          <div
            className="flex items-center justify-center text-text-muted"
            style={{ height: 200 }}
          >
            Loading settings…
          </div>
        ) : Object.keys(settings).length === 0 ? (
          <div
            className={cn(
              'bg-surface border border-sand text-text-muted italic text-center',
            )}
            style={{ borderRadius: 12, padding: '40px 16px', fontSize: 13 }}
          >
            No settings yet — create one to get started.
          </div>
        ) : (
          Object.entries(settings).map(([key, data]) => (
            <SettingCard
              key={key}
              settingKey={key}
              value={data.value}
              notes={data.notes}
              onChange={handleValueChange(key)}
              onNotesChange={handleNotesChange(key)}
              onSave={handleSave(key)}
              onDelete={handleDelete(key)}
              onUpload={handleImageUpload(key)}
              onRemoveImage={handleImageRemove(key)}
            />
          ))
        )}
      </div>

      <Modal
        open={newDialogOpen}
        onClose={() => {
          if (creating) return;
          setNewDialogOpen(false);
          setNewName('');
        }}
        eyebrow="General App Settings"
        title="Create new setting"
        maxWidth={520}
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => {
                setNewDialogOpen(false);
                setNewName('');
              }}
              disabled={creating}
            >
              Cancel
            </Button>
            <Button
              variant="coral"
              onClick={handleCreateNew}
              disabled={!newName.trim() || creating}
            >
              {creating ? 'Creating…' : 'Create'}
            </Button>
          </>
        }
      >
        <FieldBlock
          label="Key name"
          required
          hint="Used as the lookup key in app code"
          htmlFor="f-new-setting-name"
          noMargin
        >
          <TextInput
            id="f-new-setting-name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="e.g. promo_banner_dismissal_days"
            autoFocus
          />
        </FieldBlock>
      </Modal>
    </div>
  );
}
